import React from "react";
import * as Yup from "yup";
import { useFormik as useFormic } from "formik";

import logoLight from "assets/images/logo-light.png";
import logoDark from "assets/images/logo-dark.png";
import { loginUser } from "slices/thunk";
import { useDispatch, useSelector } from "react-redux";
import withRouter from "Common/withRouter";
import { createSelector } from 'reselect';
import AuthIcon from "pages/AuthenticationInner/AuthIcon";
import { Link } from "react-router-dom";

const Login = (props: any) => {
  document.title = "Login | Crediflow - CrediApoyo";

  const dispatch = useDispatch<any>();

  const selectLogin = createSelector(
    (state: any) => state.Login,
    (login) => ({
      isAuthenticated: login.isAuthenticated,
      error: login.error
    })
  );

  const { isAuthenticated, error } = useSelector(selectLogin);

  const rememberedEmail = localStorage.getItem("rememberedEmail");

  const validation: any = useFormic({
    enableReinitialize: true,
    initialValues: {
      email: rememberedEmail || '',
      password: '',
      remember: !!rememberedEmail,
    },
    validationSchema: Yup.object({
      email: Yup.string().required("El usuario es requerido"),
      password: Yup.string().required("La contraseña es requerida"),
    }),
    onSubmit: (values: any) => {
      if (values.remember) {
        localStorage.setItem("rememberedEmail", values.email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      dispatch(loginUser(values, props.router.navigate));
    }
  });

  React.useEffect(() => {
    const bodyElement = document.body;

    bodyElement.classList.add('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'lg:py-10', 'bg-slate-50', 'dark:bg-zink-800', 'dark:text-zink-100', 'font-public');

    return () => {
      bodyElement.classList.remove('flex', 'items-center', 'justify-center', 'min-h-screen', 'py-16', 'lg:py-10', 'bg-slate-50', 'dark:bg-zink-800', 'dark:text-zink-100', 'font-public');
    }
  }, []);

  return (
    <React.Fragment>
      <div className="relative">
        <AuthIcon />
        <div className="mb-0 w-screen lg:mx-auto lg:w-[500px] card shadow-lg border-none shadow-slate-100 relative">
          <div className="!px-10 !py-12 card-body">
            <Link to="/">
              <img src={logoLight} alt="" className="hidden mx-auto dark:block" />
              <img src={logoDark} alt="" className="block mx-auto dark:hidden" />
            </Link>

            <div className="mt-8 text-center">
              <h4 className="mb-1 text-custom-500 dark:text-custom-500">Bienvenido de nuevo</h4>
              <p className="text-slate-500 dark:text-zink-200">Inicia sesión para acceder a tu cuenta</p>
            </div>

            <form className="mt-10" id="signInForm"
              onSubmit={(event: any) => {
                event.preventDefault();
                validation.handleSubmit();
                return false;
              }}>
              {isAuthenticated && <div className="px-4 py-3 mb-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50">
                Has iniciado sesión correctamente.
              </div>}
              {error && <div className="px-4 py-3 mb-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50">
                No se ha podido iniciar sesión. {error}
              </div>}

              <div className="mb-3">
                <label htmlFor="email" className="inline-block mb-2 text-base font-medium">Usuario</label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Ingresa usuario"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email || ""}
                />
                {validation.touched.email && validation.errors.email ? (
                  <div id="email-error" className="mt-1 text-sm text-red-500">{validation.errors.email}</div>
                ) : null}
              </div>

              <div className="mb-3">
                <label htmlFor="password" className="inline-block mb-2 text-base font-medium">Contraseña</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Ingresa contraseña"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.password || ""}
                />
                {validation.touched.password && validation.errors.password ? (
                  <div id="password-error" className="mt-1 text-sm text-red-500">{validation.errors.password}</div>
                ) : null}
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <input
                    id="checkboxDefault1"
                    name="remember"
                    type="checkbox"
                    onChange={validation.handleChange}
                    checked={validation.values.remember}
                    className="size-4 border rounded-sm appearance-none bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500 checked:disabled:bg-custom-400 checked:disabled:border-custom-400"
                  />
                  <label htmlFor="checkboxDefault1" className="inline-block text-base font-medium align-middle cursor-pointer">Recordarme</label>
                </div>
              </div>

              <div className="mt-10">
                <button type="submit" className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">Iniciar sesión</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Login);
